"use client";

import { useEffect, useRef } from "react";
import { datadogRum } from "@datadog/browser-rum";
import {
  HIDDEN_TAB_REPLAY_PAUSE_MS,
  isDatadogRumConfigured,
} from "@/lib/observability/datadog-rum";

/**
 * Pauses session replay recording when the tab has been hidden for
 * HIDDEN_TAB_REPLAY_PAUSE_MS (5 minutes) and resumes it when the tab
 * becomes visible again — but only if this component was the one that
 * stopped recording. This prevents long-lived hidden tabs from accumulating
 * replay memory and avoids force-starting replay for sampled-out users.
 * Renders nothing.
 */
export function SessionReplayLifecycle() {
  const stoppedByUs = useRef(false);
  const pauseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isDatadogRumConfigured()) return;

    function clearPauseTimer() {
      if (pauseTimerRef.current !== null) {
        clearTimeout(pauseTimerRef.current);
        pauseTimerRef.current = null;
      }
    }

    function handleVisibilityChange() {
      if (document.visibilityState === "hidden") {
        pauseTimerRef.current = setTimeout(() => {
          datadogRum.stopSessionReplayRecording();
          stoppedByUs.current = true;
          pauseTimerRef.current = null;
        }, HIDDEN_TAB_REPLAY_PAUSE_MS);
      } else {
        clearPauseTimer();
        if (stoppedByUs.current) {
          datadogRum.startSessionReplayRecording();
          stoppedByUs.current = false;
        }
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      clearPauseTimer();
    };
  }, []);

  return null;
}
